import { useCallback, useEffect, useMemo, useState, type ReactNode, type FormEvent } from 'react';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  listBrandingTypes,
  listBrandingItems,
  listUoms,
  createBrandingItem,
  updateBrandingItem,
  deactivateBrandingItem,
  reactivateBrandingItem,
  type BrandingTypeRow,
  type BrandingItemRow,
  type UomRow,
} from '../../api/brandingMasterApi.js';
import {
  brandingUomForPlan,
  type BrandingMeasurementPlan,
} from '../../../../shared/contracts/branding-line.js';
import { MAP_SHAPES, markerSvg } from '../../../../shared/contracts/map-activity.js';

// The four activity types and the subtypes under them. Every branding request
// picks from this list, so a subtype that is missing here cannot be requested
// at all — which is why adding one had to stop being a code change.

const CARD: React.CSSProperties = {
  padding: 16,
  borderRadius: 8,
  marginBottom: 16,
  border: '1px solid var(--color-border-default)',
  background: 'var(--color-bg-elevated)',
};

const MUTED: React.CSSProperties = { color: 'var(--color-text-muted)' };

const ITEM_CODE_RE = /^[A-Z][A-Z0-9_]{1,49}$/;

// A sub-type says what each of a line's three dimensions measures. The first
// two are lengths and multiply into the amount; the third is time and does not.
const LENGTHS = ['HEIGHT', 'WIDTH', 'NA'] as const;
const TIMES = ['DURATION', 'NA'] as const;
const MEASURE_LABEL: Record<string, string> = {
  HEIGHT: 'Height',
  WIDTH: 'Width',
  DURATION: 'Duration (seconds)',
  NA: 'Not applicable',
};

function describeMeasure(i: BrandingItemRow): string {
  const named = [i.dim1Meaning, i.dim2Meaning, i.dim3Meaning]
    .filter((m) => m && m !== 'NA')
    .map((m) => MEASURE_LABEL[m].toLowerCase());
  return named.length > 0 ? named.join(' by ') : 'a plain count';
}

const SHAPE_LABEL: Record<string, string> = {
  CIRCLE: 'Circle',
  SQUARE: 'Square',
  TRIANGLE: 'Triangle',
  DIAMOND: 'Diamond',
  STAR: 'Star',
  PENTAGON: 'Pentagon',
  HEXAGON: 'Hexagon',
  CROSS: 'Cross',
};

export function BrandingMasterPage(): ReactNode {
  const { token } = useAuth();
  const [types, setTypes] = useState<readonly BrandingTypeRow[] | null>(null);
  const [items, setItems] = useState<readonly BrandingItemRow[]>([]);
  const [uoms, setUoms] = useState<readonly UomRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  // Which type the new-subtype form is open under, and what it holds.
  const [addFor, setAddFor] = useState<string | null>(null);
  const [itemCode, setItemCode] = useState('');
  const [itemName, setItemName] = useState('');
  const [uomId, setUomId] = useState('');
  const [needsDealer, setNeedsDealer] = useState(false);
  const [dim1, setDim1] = useState<string>('NA');
  const [dim2, setDim2] = useState<string>('NA');
  const [dim3, setDim3] = useState<string>('NA');
  const [newHsn, setNewHsn] = useState('');
  // Only the row being edited is held here; everything else reads from the list.
  const [hsnEdit, setHsnEdit] = useState<{ id: string; value: string } | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [t, i, u] = await Promise.all([
        listBrandingTypes(token),
        // The box decides what the server returns, not what the page hides
        // afterwards: withdrawn rows never left the server, so filtering here
        // could only ever hide, never reveal.
        listBrandingItems(token, 500, showInactive),
        listUoms(token),
      ]);
      setTypes(t.data ?? []);
      setItems(i.data ?? []);
      setUoms((u.data ?? []).filter((x) => x.recordStatusCode === 'ACTIVE'));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token, showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const visibleTypes = useMemo(
    () => (types ?? []).filter((t) => showInactive || t.recordStatusCode === 'ACTIVE'),
    [types, showInactive],
  );

  const subtypesOf = useCallback(
    (typeId: string) =>
      items
        .filter((i) => i.brandingTypeId === typeId)
        .filter((i) => showInactive || i.recordStatusCode === 'ACTIVE')
        .slice()
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.itemCode.localeCompare(b.itemCode)),
    [items, showInactive],
  );

  function openAdd(type: BrandingTypeRow) {
    setAddFor(type.brandingTypeId);
    // Prefixing with the type keeps codes readable and collision-free, the way
    // the seeded ones already read (BTL_WALL_PAINT, POP_STANDEE).
    setItemCode(`${type.typeCode}_`);
    setItemName('');
    setUomId('');
    setNeedsDealer(false);
    setDim1('NA');
    setDim2('NA');
    setDim3('NA');
    setNewHsn('');
    setFormError(null);
  }

  async function submitAdd(e: FormEvent) {
    e.preventDefault();
    if (!addFor) return;
    const code = itemCode.trim().toUpperCase();
    if (!ITEM_CODE_RE.test(code)) {
      setFormError('Code must start with a letter and use only A–Z, 0–9 and _.');
      return;
    }
    if (!itemName.trim()) {
      setFormError('Name is required.');
      return;
    }
    if (!uomId) {
      setFormError('Pick how this subtype is measured.');
      return;
    }
    setBusy(true);
    setFormError(null);
    setNotice(null);
    try {
      await createBrandingItem(
        {
          brandingTypeId: addFor,
          itemCode: code,
          itemName: itemName.trim(),
          uomId,
          dealerReferenceRequired: needsDealer,
          dim1Meaning: dim1,
          dim2Meaning: dim2,
          dim3Meaning: dim3,
          ...(newHsn.trim() ? { hsnSacCode: newHsn.trim() } : {}),
        },
        token,
      );
      setNotice(`${code} added.`);
      setAddFor(null);
      await load();
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleItem(item: BrandingItemRow) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (item.recordStatusCode === 'ACTIVE') {
        await deactivateBrandingItem(item.brandingItemId, token);
        setNotice(`${item.itemCode} withdrawn. Past requests that used it are untouched.`);
      } else {
        await reactivateBrandingItem(item.brandingItemId, token);
        setNotice(`${item.itemCode} is available again.`);
      }
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveHsn(item: BrandingItemRow) {
    const code = (hsnEdit?.value ?? '').trim();
    if (!code) {
      setError('Enter the HSN or SAC code, or press Cancel.');
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await updateBrandingItem(item.brandingItemId, { hsnSacCode: code }, token);
      setHsnEdit(null);
      setNotice(`${item.itemCode} is now classified under ${code}.`);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function saveItem(
    item: BrandingItemRow,
    patch: Parameters<typeof updateBrandingItem>[1],
    done: string,
  ) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await updateBrandingItem(item.brandingItemId, patch, token);
      setNotice(done);
      await load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function saveMeasure(item: BrandingItemRow, patch: Record<string, string>) {
    return saveItem(
      item,
      patch,
      `${item.itemCode} is now measured as ${describeMeasure({ ...item, ...patch } as BrandingItemRow)}.`,
    );
  }

  const uomOf = (id: string | null) => uoms.find((u) => u.uomId === id)?.uomCode ?? '—';

  /**
   * Choosing what a sub-type measures also chooses the unit it is measured in,
   * because the two answer the same question and a screen that let them
   * disagree would be a screen that lied.
   */
  function setDimension(patch: { dim1?: string; dim2?: string; dim3?: string }) {
    const next = { dim1: patch.dim1 ?? dim1, dim2: patch.dim2 ?? dim2, dim3: patch.dim3 ?? dim3 };
    if (patch.dim1 !== undefined) setDim1(patch.dim1);
    if (patch.dim2 !== undefined) setDim2(patch.dim2);
    if (patch.dim3 !== undefined) setDim3(patch.dim3);
    const wanted = brandingUomForPlan(
      next as BrandingMeasurementPlan,
      uoms.find((u) => u.uomId === uomId)?.uomCode ?? null,
    );
    const unit = uoms.find((u) => u.uomCode === wanted);
    if (unit) setUomId(unit.uomId);
  }

  return (
    <main aria-label="branding-master">
      <h1 style={{ fontSize: 20, marginBottom: 4 }}>Branding activity types</h1>
      <p style={{ ...MUTED, marginTop: 0, marginBottom: 16 }}>
        The four activity types and the subtypes under each. A branding request can
        only ask for something on this list, so add a subtype here before it is
        needed. A subtype is withdrawn rather than deleted — requests that already
        used it keep reading correctly.
      </p>

      <label style={{ display: 'block', marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={showInactive}
          onChange={(e) => setShowInactive(e.target.checked)}
          aria-label="show-withdrawn"
        />{' '}
        Show withdrawn
      </label>

      {error ? <p role="alert">{error}</p> : null}
      {notice ? <p role="status">{notice}</p> : null}

      {!types ? (
        <p>Loading…</p>
      ) : visibleTypes.length === 0 ? (
        <p>No branding types are configured for this tenant.</p>
      ) : (
        visibleTypes.map((t) => {
          const subs = subtypesOf(t.brandingTypeId);
          return (
            <section key={t.brandingTypeId} style={CARD} aria-label={`type-${t.typeCode}`}>
              <h2 style={{ fontSize: 16, margin: 0 }}>
                {t.typeName}{' '}
                <span style={MUTED}>({t.typeCode})</span>
                {t.recordStatusCode !== 'ACTIVE' ? (
                  <span style={MUTED}> — {t.recordStatusCode.toLowerCase()}</span>
                ) : null}
              </h2>
              <p style={{ ...MUTED, marginTop: 4 }}>
                {subs.length} subtype{subs.length === 1 ? '' : 's'}
              </p>

              {subs.length > 0 ? (
                <table aria-label={`subtypes-${t.typeCode}`} style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th scope="col">Type</th>
                      <th scope="col">Subtype</th>
                      <th scope="col">Code</th>
                      <th scope="col">HSN / SAC</th>
                      <th scope="col">Measured in</th>
                      <th scope="col">Dimension 1</th>
                      <th scope="col">Dimension 2</th>
                      <th scope="col">Dimension 3</th>
                      <th scope="col">Dealer needed</th>
                      <th scope="col">Photo needed</th>
                      <th scope="col">Position needed</th>
                      <th scope="col">Map symbol</th>
                      <th scope="col">Status</th>
                      <th scope="col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map((i) => (
                      <tr key={i.brandingItemId}>
                        <td>{t.typeCode}</td>
                        <td>{i.itemName}</td>
                        <td>{i.itemCode}</td>
                        {/* The code is held in the GST classification chain, not
                            on this row; this is only where it is decided. */}
                        <td data-testid={`hsn-${i.itemCode}`}>
                          {hsnEdit?.id === i.brandingItemId ? (
                            <>
                              <input
                                aria-label={`HSN or SAC for ${i.itemCode}`}
                                value={hsnEdit.value}
                                inputMode="numeric"
                                maxLength={8}
                                onChange={(e) =>
                                  setHsnEdit({ id: i.brandingItemId, value: e.target.value })
                                }
                              />
                              <button
                                type="button"
                                aria-label={`save-hsn-${i.itemCode}`}
                                onClick={() => void saveHsn(i)}
                                disabled={busy}
                              >
                                Save
                              </button>
                              <button type="button" onClick={() => setHsnEdit(null)} disabled={busy}>
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <span>
                                {i.hsnSacCode
                                  ? `${i.hsnSacType ?? ''} ${i.hsnSacCode}`.trim()
                                  : 'Not set'}
                              </span>{' '}
                              <button
                                type="button"
                                aria-label={`set-hsn-${i.itemCode}`}
                                onClick={() =>
                                  setHsnEdit({ id: i.brandingItemId, value: i.hsnSacCode ?? '' })
                                }
                                disabled={busy}
                              >
                                {i.hsnSacCode ? 'Change' : 'Add'}
                              </button>
                            </>
                          )}
                        </td>
                        {/* Not chosen here: the unit follows from the three
                            declarations to its right. */}
                        <td data-testid={`unit-${i.itemCode}`}>{uomOf(i.uomId)}</td>
                        <td data-testid={`measure-${i.itemCode}`}>
                          <select
                            aria-label={`First measurement for ${i.itemCode}`}
                            value={i.dim1Meaning}
                            disabled={busy}
                            onChange={(e) => void saveMeasure(i, { dim1Meaning: e.target.value })}
                          >
                            {LENGTHS.map((m) => (
                              <option key={m} value={m}>{MEASURE_LABEL[m]}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            aria-label={`Second measurement for ${i.itemCode}`}
                            value={i.dim2Meaning}
                            disabled={busy}
                            onChange={(e) => void saveMeasure(i, { dim2Meaning: e.target.value })}
                          >
                            {LENGTHS.map((m) => (
                              <option key={m} value={m}>{MEASURE_LABEL[m]}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <select
                            aria-label={`Third measurement for ${i.itemCode}`}
                            value={i.dim3Meaning}
                            disabled={busy}
                            onChange={(e) => void saveMeasure(i, { dim3Meaning: e.target.value })}
                          >
                            {TIMES.map((m) => (
                              <option key={m} value={m}>{MEASURE_LABEL[m]}</option>
                            ))}
                          </select>
                        </td>
                        <td data-testid={`dealer-rule-${i.itemCode}`}>
                          {i.dealerReferenceRequired ? 'Required' : 'Not needed'}
                        </td>
                        {/* What a completion of this sub-type must carry, and
                            how it is drawn on the activity map. */}
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Photo needed for ${i.itemCode}`}
                            checked={i.requiresPhoto}
                            disabled={busy}
                            onChange={(e) =>
                              void saveItem(
                                i,
                                { requiresPhoto: e.target.checked },
                                `${i.itemCode} ${e.target.checked ? 'now needs' : 'no longer needs'} a photograph to be completed.`,
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`Position needed for ${i.itemCode}`}
                            checked={i.requiresGps}
                            disabled={busy}
                            onChange={(e) =>
                              void saveItem(
                                i,
                                { requiresGps: e.target.checked },
                                `${i.itemCode} ${e.target.checked ? 'now needs' : 'no longer needs'} the position it was done at.`,
                              )
                            }
                          />
                        </td>
                        <td>
                          <img
                            alt=""
                            width={18}
                            height={18}
                            src={`data:image/svg+xml;utf8,${encodeURIComponent(markerSvg(i.mapShape, '#9E9E9E', 18))}`}
                          />{' '}
                          <select
                            aria-label={`Map symbol for ${i.itemCode}`}
                            value={i.mapShape}
                            disabled={busy}
                            onChange={(e) =>
                              void saveItem(
                                i,
                                { mapShape: e.target.value },
                                `${i.itemCode} is now drawn as a ${SHAPE_LABEL[e.target.value].toLowerCase()} on the map.`,
                              )
                            }
                          >
                            {MAP_SHAPES.map((s) => (
                              <option key={s} value={s}>{SHAPE_LABEL[s]}</option>
                            ))}
                          </select>
                        </td>
                        <td>{i.recordStatusCode === 'ACTIVE' ? 'Available' : 'Withdrawn'}</td>
                        <td>
                          <button
                            type="button"
                            aria-label={`toggle-${i.itemCode}`}
                            onClick={() => void toggleItem(i)}
                            disabled={busy}
                          >
                            {i.recordStatusCode === 'ACTIVE' ? 'Withdraw' : 'Restore'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : null}

              {addFor === t.brandingTypeId ? (
                <form onSubmit={submitAdd} aria-label={`add-subtype-${t.typeCode}`}>
                  {formError ? <p role="alert">{formError}</p> : null}
                  <label htmlFor={`code-${t.typeCode}`}>Code</label>
                  <input
                    id={`code-${t.typeCode}`}
                    value={itemCode}
                    onChange={(e) => setItemCode(e.target.value.toUpperCase())}
                  />
                  <label htmlFor={`name-${t.typeCode}`}>Name</label>
                  <input
                    id={`name-${t.typeCode}`}
                    value={itemName}
                    onChange={(e) => setItemName(e.target.value)}
                  />
                  <label htmlFor={`dim1-${t.typeCode}`}>Dimension 1</label>
                  <select
                    id={`dim1-${t.typeCode}`}
                    value={dim1}
                    onChange={(e) => setDimension({ dim1: e.target.value })}
                  >
                    {LENGTHS.map((m) => (
                      <option key={m} value={m}>{MEASURE_LABEL[m]}</option>
                    ))}
                  </select>
                  <label htmlFor={`dim2-${t.typeCode}`}>Dimension 2</label>
                  <select
                    id={`dim2-${t.typeCode}`}
                    value={dim2}
                    onChange={(e) => setDimension({ dim2: e.target.value })}
                  >
                    {LENGTHS.map((m) => (
                      <option key={m} value={m}>{MEASURE_LABEL[m]}</option>
                    ))}
                  </select>
                  <label htmlFor={`dim3-${t.typeCode}`}>Dimension 3</label>
                  <select
                    id={`dim3-${t.typeCode}`}
                    value={dim3}
                    onChange={(e) => setDimension({ dim3: e.target.value })}
                  >
                    {TIMES.map((m) => (
                      <option key={m} value={m}>{MEASURE_LABEL[m]}</option>
                    ))}
                  </select>
                  {/* Chosen only to name the answer on the bill; which unit is
                      allowed follows from the three above. */}
                  <label htmlFor={`uom-${t.typeCode}`}>Unit on the bill</label>
                  <select
                    id={`uom-${t.typeCode}`}
                    value={uomId}
                    onChange={(e) => setUomId(e.target.value)}
                  >
                    <option value="">Select a unit</option>
                    {uoms.map((u) => (
                      <option key={u.uomId} value={u.uomId}>
                        {u.uomCode} — {u.uomName}
                      </option>
                    ))}
                  </select>
                  <label htmlFor={`hsn-${t.typeCode}`}>HSN / SAC code</label>
                  <input
                    id={`hsn-${t.typeCode}`}
                    value={newHsn}
                    inputMode="numeric"
                    maxLength={8}
                    placeholder="e.g. 998364"
                    onChange={(e) => setNewHsn(e.target.value)}
                  />
                  {/* Ticked only for work done on the dealer's own property.
                      A request for such a subtype cannot be saved without
                      naming him; everything else files against the tenant's
                      placeholder dealer. */}
                  <label htmlFor={`dealer-${t.typeCode}`}>
                    <input
                      id={`dealer-${t.typeCode}`}
                      type="checkbox"
                      checked={needsDealer}
                      onChange={(e) => setNeedsDealer(e.target.checked)}
                    />{' '}
                    Done at a dealer&rsquo;s premises — the request must name him
                  </label>
                  <button type="submit" aria-label={`save-subtype-${t.typeCode}`} disabled={busy}>
                    Add subtype
                  </button>
                  <button type="button" onClick={() => setAddFor(null)} disabled={busy}>
                    Cancel
                  </button>
                </form>
              ) : (
                <button
                  type="button"
                  aria-label={`add-subtype-to-${t.typeCode}`}
                  onClick={() => openAdd(t)}
                  disabled={busy || t.recordStatusCode !== 'ACTIVE'}
                >
                  Add a subtype to {t.typeName}
                </button>
              )}
            </section>
          );
        })
      )}
    </main>
  );
}
